import { useMemo, useState } from 'react';
import {
  Badge,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import { IconChartBar, IconClock, IconGitBranch, IconPhone, IconPhoneCheck, IconPhoneX } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { useOrg } from '@/hooks/use-org';
import { CallDetailModal } from './-components/call-detail-modal';
import { CallVolumeSeriesSubscription, FlowCallStatsSubscription } from './-components/call-graphql';
import { callStatusColor, formatDuration } from './-components/format';
import { RecentCallsTable } from './-components/recent-calls-table';
import { StatCard } from './-components/stat-cards';
import { useCallStats } from './-components/use-call-stats';
import { useSinceDays } from './-components/use-time-window';

const STATS_WINDOW_DAYS = 30;
const FLOW_STATS_WINDOW_DAYS = 90;

export const Route = createFileRoute('/_authed/analytics')({
  component: AnalyticsPage,
});

function successRateColor(rate: number) {
  return rate >= 80 ? 'green' : rate >= 50 ? 'yellow' : 'red';
}

function successRateOf(completed: number, total: number) {
  return total > 0 ? Math.round((completed / total) * 100) : 0;
}

function CallVolumeChart({ organizationId, startedAt }: { organizationId: string; startedAt: string }) {
  const [{ data }] = useSubscription({ query: CallVolumeSeriesSubscription, variables: { organizationId, startedAt } });

  const series = useMemo(() => {
    const byDay = new Map<string, { calls: number; completed: number }>();

    for (const log of data?.callLogs ?? []) {
      const day = log.startedAt.slice(0, 10);
      const bucket = byDay.get(day) ?? { calls: 0, completed: 0 };

      byDay.set(day, {
        calls: bucket.calls + 1,
        completed: bucket.completed + (log.status === 'completed' || log.status === 'reaped' ? 1 : 0),
      });
    }

    return [...byDay.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([day, bucket]) => ({ date: day.slice(5), ...bucket }));
  }, [data]);

  return (
    <Paper p="lg" radius="lg" withBorder>
      <Group gap="xs" mb="md">
        <IconChartBar size={20} stroke={1.5} style={{ color: 'var(--mantine-primary-color-filled)' }} />
        <Text fw={600}>Daily Call Volume</Text>
      </Group>
      {!data ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : series.length === 0 ? (
        <Center py="xl">
          <Text c="dimmed">No call data yet</Text>
        </Center>
      ) : (
        <AreaChart
          h={220}
          data={series}
          dataKey="date"
          withLegend
          curveType="monotone"
          series={[
            { name: 'calls', label: 'Total', color: 'indigo.6' },
            { name: 'completed', label: 'Completed', color: 'green.6' },
          ]}
        />
      )}
    </Paper>
  );
}

function FlowPerformance({ organizationId, startedAt }: { organizationId: string; startedAt: string }) {
  const [{ data }] = useSubscription({ query: FlowCallStatsSubscription, variables: { organizationId, startedAt } });

  const flows = (data?.flows ?? [])
    .map((flow) => ({
      id: flow.id,
      name: flow.name,
      totalCalls: flow.total.aggregate?.count ?? 0,
      completedCalls: flow.completed.aggregate?.count ?? 0,
      averageDurationSeconds: Math.round(flow.total.aggregate?.avg?.durationSeconds ?? 0),
    }))
    .filter((flow) => flow.totalCalls > 0);

  return (
    <Paper p="lg" radius="lg" withBorder>
      <Group gap="xs" mb="md">
        <IconGitBranch size={20} stroke={1.5} style={{ color: 'var(--mantine-primary-color-filled)' }} />
        <Text fw={600}>Flow Performance</Text>
      </Group>
      {flows.length === 0 ? (
        <Center py="xl">
          <Text c="dimmed">No flow data yet</Text>
        </Center>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Flow</Table.Th>
              <Table.Th ta="right">Total Calls</Table.Th>
              <Table.Th ta="right">Completed</Table.Th>
              <Table.Th ta="right">Success Rate</Table.Th>
              <Table.Th ta="right">Avg Duration</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {flows.map((flow) => (
              <Table.Tr key={flow.id}>
                <Table.Td>
                  <Text fw={500}>{flow.name}</Text>
                </Table.Td>
                <Table.Td ta="right">{flow.totalCalls}</Table.Td>
                <Table.Td ta="right">{flow.completedCalls}</Table.Td>
                <Table.Td ta="right">
                  <Badge variant="light" color={successRateColor(successRateOf(flow.completedCalls, flow.totalCalls))}>
                    {successRateOf(flow.completedCalls, flow.totalCalls)}%
                  </Badge>
                </Table.Td>
                <Table.Td ta="right">{formatDuration(flow.averageDurationSeconds)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Paper>
  );
}

function AnalyticsPage() {
  const { organizationId, currentOrg, isLoading } = useOrg();
  const [selectedCallControlId, setSelectedCallControlId] = useState<string | null>(null);

  const isAdmin = currentOrg?.role === 'owner' || currentOrg?.role === 'admin';
  const statsWindow = useSinceDays(STATS_WINDOW_DAYS);
  const flowStatsWindow = useSinceDays(FLOW_STATS_WINDOW_DAYS);
  const stats = useCallStats(organizationId, statsWindow);

  if (isLoading || (organizationId && !stats)) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId || !stats) {
    return (
      <Center h="50vh">
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  const successRate = successRateOf(stats.completedCalls, stats.totalCalls);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Call Analytics</Title>
          <Text c="dimmed" mt="xs">
            Monitor your call flow performance
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
          <StatCard title="Total Calls" value={stats.totalCalls.toString()} icon={IconPhone} color="indigo" />
          <StatCard title="Completed" value={stats.completedCalls.toString()} icon={IconPhoneCheck} color="green" />
          <StatCard title="Failed" value={stats.failedCalls.toString()} icon={IconPhoneX} color="red" />
          <StatCard
            title="Avg Duration"
            value={formatDuration(stats.averageDurationSeconds)}
            icon={IconClock}
            color="indigo"
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          <Paper p="lg" radius="lg" withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={600}>Success Rate</Text>
              <Badge variant="light" color={successRateColor(successRate)}>
                {successRate}%
              </Badge>
            </Group>
            <Center>
              <RingProgress
                size={180}
                thickness={16}
                roundCaps
                sections={[
                  { value: successRate, color: 'green' },
                  { value: 100 - successRate, color: 'gray.2' },
                ]}
                label={
                  <Center>
                    <Stack gap={0} align="center">
                      <Text size="xl" fw={700}>
                        {successRate}%
                      </Text>
                      <Text size="xs" c="dimmed">
                        Success
                      </Text>
                    </Stack>
                  </Center>
                }
              />
            </Center>
          </Paper>

          <Paper p="lg" radius="lg" withBorder>
            <Group gap="xs" mb="md">
              <IconChartBar size={20} stroke={1.5} style={{ color: 'var(--mantine-primary-color-filled)' }} />
              <Text fw={600}>Status Breakdown</Text>
            </Group>
            <Stack gap="sm">
              {stats.byStatus.map((entry) => (
                <div key={entry.status}>
                  <Group justify="space-between" mb={4}>
                    <Text size="sm" tt="capitalize">
                      {entry.status.replace('_', ' ')}
                    </Text>
                    <Text size="sm" fw={500}>
                      {entry.count}
                    </Text>
                  </Group>
                  <Progress
                    value={stats.totalCalls > 0 ? (entry.count / stats.totalCalls) * 100 : 0}
                    color={callStatusColor(entry.status)}
                    size="sm"
                    radius="xl"
                  />
                </div>
              ))}
              {stats.byStatus.length === 0 && (
                <Text c="dimmed" size="sm" ta="center">
                  No call data yet
                </Text>
              )}
            </Stack>
          </Paper>
        </SimpleGrid>

        <CallVolumeChart organizationId={organizationId} startedAt={statsWindow} />

        <FlowPerformance organizationId={organizationId} startedAt={flowStatsWindow} />

        <Paper p="lg" radius="lg" withBorder>
          <Group gap="xs" mb="md">
            <IconPhone size={20} stroke={1.5} style={{ color: 'var(--mantine-primary-color-filled)' }} />
            <Text fw={600}>Recent Calls</Text>
          </Group>
          <RecentCallsTable
            organizationId={organizationId}
            limit={10}
            onSelect={isAdmin ? setSelectedCallControlId : undefined}
          />
        </Paper>
      </Stack>

      {isAdmin && (
        <CallDetailModal
          organizationId={organizationId}
          callControlId={selectedCallControlId}
          onClose={() => setSelectedCallControlId(null)}
        />
      )}
    </Container>
  );
}
